import { Component, ChangeDetectorRef, Inject, OnInit } from '@angular/core';
import { lastValueFrom } from '@services';
import { RmsModule } from '@rms';
import { OutboundDataService } from '../../../../../services/outbound-data.service';
import { OrdersDataService } from '../../../../../services/orders-data.service';
import { ReportService } from '../../../../../services/report.service';
import { AuthService } from '../../../../../services/auth.service';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { PopupService } from '../../../../../services/popup.service';

@Component({
  selector: 'app-outbound-order-detail',
  imports: [RmsModule],
  templateUrl: './outbound-order-detail.component.html',
  styleUrl: './outbound-order-detail.component.scss'
})
export class OutboundOrderDetailComponent implements OnInit {
  OrderID!: number;

  OrderDetails: any = null;
  Documents: any[] = [];
  TransmissionHistory: any[] = [];
  ActionHistory: any[] = [];
  LineItems: any[] = [];
  get lineWarnings(): string[] {
    return this.LineItems.flatMap(line => {
      const warnings: string[] = [];
      if (line.Price == null || Number(line.Price) <= 0) warnings.push(`Reference ${line.InventoryID} has a missing or zero price.`);
      if (line.WarehouseID == null || line.WarehouseID !== this.OrderDetails?.WarehouseNumber) warnings.push(`Reference ${line.InventoryID} does not match the order warehouse.`);
      return warnings;
    });
  }

  private readonly ExpectedDocumentTypes = ['Delivery Order', 'Bill of Lading'];

  // 5.2.9: only these Azure AD groups can authorize a zero-price shipment —
  // same isInAnyGroup() pattern CustomerManagementComponent's EDITOR_GROUPS
  // already uses, so this checks real AD group membership, not anything
  // stored in this app's own database.
  private readonly ZERO_PRICE_OVERRIDE_GROUPS = ['Executive', 'System Admins'];

  BlockedDates: string[] = [];

  Loading = true;
  Saving = false;
  Generating = false;
  SavingShipTo = false;
  Working = false;
  ActionError = '';
  private savedFreight = '';
  get busy(): boolean { return this.Loading || this.Saving || this.Generating || this.SavingShipTo || this.Working; }
  get freightChanged(): boolean { return this.savedFreight !== this.freightSnapshot(); }
  private freightSnapshot(): string { return JSON.stringify([this.TruckingRate, this.TruckingQuote, this.WarehouseInstructions, this.BOLInstructions]); }
  async runAction(action: () => Promise<void>) {
    if (this.busy) return;
    this.Working = true;
    this.ActionError = '';
    try { await action(); } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'The action could not be completed. Please retry.';
    } finally { this.Working = false; this.cdr.markForCheck(); }
  }

  TruckingRate = '';
  TruckingQuote = '';
  WarehouseInstructions = '';
  BOLInstructions = '';
  PickupDate: string | null = null;

  // ---- Ship-to address picker (5.2.14) ----
  // ShippingAddresses lists every address on this order (from 5.1's
  // multi-address support). SelectedShipToAddressID is which one Outbound
  // has picked for THIS shipment — separate from IsPrimary, which is just
  // the customer's default and not a per-shipment choice.
  ShippingAddresses: any[] = [];
  SelectedShipToAddressID: number | null = null;

  AvailableContacts: any[] = [];
  SelectedContactIds = new Set<number>();
  SendingDocumentType: string | null = null;

  // Inline add-contact form state (5.2.18), shown inside the recipient
  // picker when the selected warehouse has no contacts configured yet.
  AddingContact = false;
  NewContactName = '';
  NewContactEmail = '';

  constructor(
    private outboundData: OutboundDataService,
    private ordersData: OrdersDataService,
    private reportService: ReportService,
    private pulldownRef: RmsPulldownRef,
    private cdr: ChangeDetectorRef,
    private popupService: PopupService,
    private auth: AuthService,
    @Inject(RMS_PULLDOWN_DATA) private pulldownData: { OrderID: number }
  ) { }

  // UI visibility only. The installed override procedure does not enforce
  // group membership; authenticated server authorization remains required.
  get canOverrideZeroPrice(): boolean {
    return this.auth.isInAnyGroup(this.ZERO_PRICE_OVERRIDE_GROUPS);
  }

  async ngOnInit() {
    this.OrderID = this.pulldownData.OrderID;
    await this.loadAll();
  }

  async loadAll() {
    this.Loading = true;
    try {
      const [detail, history, addresses] = await Promise.all([
        lastValueFrom(this.outboundData.getOutboundOrderDetail(this.OrderID)),
        lastValueFrom(this.ordersData.getOrderHistory(this.OrderID)),
        lastValueFrom(this.outboundData.getShippingAddresses(this.OrderID))
      ]);

      this.OrderDetails = detail?.OrderDetails ?? null;
      this.LineItems = detail?.LineItems ?? [];

      const existingDocs = detail?.Documents ?? [];
      this.Documents = this.ExpectedDocumentTypes.map(type => {
        const existing = existingDocs.find((d: any) => d.DocumentType === type);
        return existing ?? { DocumentType: type, Status: 'Pending', GeneratedDate: null, OutboundDocumentID: null, FilePath: null };
      });

      this.TransmissionHistory = detail?.TransmissionHistory ?? [];
      this.ActionHistory = history ?? [];

      this.TruckingRate = this.OrderDetails?.TruckingRate?.trim() ?? '';
      this.TruckingQuote = this.OrderDetails?.TruckingQuote?.trim() ?? '';
      this.WarehouseInstructions = this.OrderDetails?.WarehouseInstructions ?? '';
      this.BOLInstructions = this.OrderDetails?.BOLInstructions ?? '';
      this.savedFreight = this.freightSnapshot();
      // Falls back to the customer's originally requested date when nothing's
      // been confirmed yet — still fully editable, this is just the starting
      // value shown, not a locked-in default.
      this.PickupDate = this.OrderDetails?.PickupDate ?? this.OrderDetails?.ProposedPickupDate ?? null;

      this.ShippingAddresses = addresses ?? [];
      this.SelectedShipToAddressID = this.OrderDetails?.ShipToAddressID
        ?? this.ShippingAddresses.find((a: any) => a.IsPrimary)?.OrderShippingAddressID
        ?? null;

      if (this.OrderDetails?.WarehouseNumber) {
        const capacity = await lastValueFrom(this.outboundData.getCapacityStatus());
        this.BlockedDates = capacity
          .filter((c: any) => c.WarehouseID === this.OrderDetails.WarehouseNumber && (c.IsClosed || c.TodayPastCutoff))
          .map((c: any) => c.PickupDate.split('T')[0]);
      } else {
        this.BlockedDates = [];
      }
    } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'Could not load this outbound order. Please retry.';
    } finally {
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  async saveFreight() {
    if (this.busy) return;
    this.Saving = true;
    this.ActionError = '';
    try {
      await lastValueFrom(this.outboundData.saveFreight({
        OrderID: this.OrderID,
        TruckingRate: this.TruckingRate,
        TruckingQuote: this.TruckingQuote,
        WarehouseInstructions: this.WarehouseInstructions,
        BOLInstructions: this.BOLInstructions
      }));
      await this.loadAll();
    } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'Freight details could not be saved.';
    } finally {
      this.Saving = false;
    }
  }

  async savePickupDate(newDate: string) {
    if (this.busy || !newDate) return;
    if (this.freightChanged) { this.ActionError = 'Save freight details before changing the pickup date.'; return; }
    this.Working = true;
    try {
      await lastValueFrom(this.outboundData.changePickupDate(this.OrderID, newDate));
    } catch (err: any) {
      this.ActionError = err?.error?.error ?? 'Selected pickup date is unavailable.';
    } finally {
      // Always reload, success or failure — this is what makes the field
      // reliably show the real saved value instead of drifting out of sync.
      await this.loadAll();
      this.Working = false;
    }
  }

  async saveShipToAddress(addressId: number) {
    if (!addressId || this.busy) return;
    if (this.freightChanged) { this.ActionError = 'Save freight details before changing the shipping address.'; return; }
    this.SavingShipTo = true;
    try {
      await lastValueFrom(this.outboundData.setShipToAddress(this.OrderID, addressId));
      await this.loadAll();
    } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'The shipping address could not be saved.';
    } finally {
      this.SavingShipTo = false;
    }
  }

  async viewDocument(type: 'Delivery Order' | 'Bill of Lading') {
    if (this.busy) return;
    if (this.freightChanged) { this.ActionError = 'Save freight details before viewing documents.'; return; }
    let doc = this.Documents.find(d => d.DocumentType === type);
    if (!doc?.FilePath || doc.Status === 'Pending') {
      await this.generateDocument(type);
      doc = this.Documents.find(d => d.DocumentType === type);
      if (!doc?.FilePath || doc.Status === 'Pending') return;
    }
    await this.runAction(() => this.previewDocument(doc.FilePath));
  }

  async generateDocument(type: 'Delivery Order' | 'Bill of Lading' | 'Warehouse Batch PDF') {
    if (this.busy) return;
    if (this.freightChanged) { this.ActionError = 'Save freight details before generating documents.'; return; }
    this.Generating = true;
    try {
      const reportType = type === 'Delivery Order' ? 'DeliveryOrder' : 'BillOfLading';
      const dataUrl = await this.reportService.generatePDF(reportType, { OrderID: this.OrderID });
      const base64 = dataUrl.split(',')[1];

      const fileName = `${type.replace(/\s+/g, '')}-${Date.now()}.pdf`;

      await lastValueFrom(this.outboundData.sendGeneratedDocument(this.OrderID, type, base64, fileName));
      await this.loadAll();
    } catch (error: any) {
      this.ActionError = error?.error?.error ?? 'The document could not be generated.';
    } finally {
      this.Generating = false;
    }
  }

  // 5.2.16/5.2.17: opens the already-generated PDF in an in-app popup
  // (RmsPopupComponent via PopupService) instead of a new browser tab —
  // keeps the person inside RMS the whole time.
  async previewDocument(filePath: string) {
    if (!filePath) return;
    const result = await lastValueFrom(this.outboundData.getDocumentPreviewUrl(filePath));
    this.popupService.openFileViewer({
      title: 'Document Preview',
      fileUrl: result.url
    });
  }

  async openSendPicker(documentType: string) {
    this.SendingDocumentType = documentType;
    await this.confirmSend();
  }

  toggleContact(contactId: number) {
    if (this.SelectedContactIds.has(contactId)) {
      this.SelectedContactIds.delete(contactId);
    } else {
      this.SelectedContactIds.add(contactId);
    }
  }

  cancelSendPicker() {
    this.SendingDocumentType = null;
    this.AvailableContacts = [];
    this.SelectedContactIds.clear();
    this.AddingContact = false;
  }

  EmailResult = '';
  async confirmSend() {
    if (this.freightChanged) { this.ActionError = 'Save freight details before sending a test document.'; return; }
    const type = this.SendingDocumentType;
    if (type !== 'Delivery Order' && type !== 'Bill of Lading') return;
    await this.runAction(async () => {
      this.EmailResult = '';
      const pdf = await this.reportService.generatePDF(type === 'Delivery Order' ? 'DeliveryOrder' : 'BillOfLading', {OrderID:this.OrderID});
      const response = await lastValueFrom(this.outboundData.sendDocument(this.OrderID, type, [], pdf.split(',')[1]));
      this.EmailResult = response?.message + ' Recipient: m.bray@royalny.com only. Warehouse delivery status is unchanged.';
      this.SendingDocumentType = null;
    });
  }

  startAddContact() {
    this.AddingContact = true;
    this.NewContactName = '';
    this.NewContactEmail = '';
  }

  async saveNewContact() {
    if (!this.NewContactEmail?.trim() || !this.OrderDetails?.WarehouseNumber) return;

    await lastValueFrom(this.outboundData.mergeWarehouseContact({
      LocationID: this.OrderDetails.WarehouseNumber,
      ContactName: this.NewContactName,
      ContactEmail: this.NewContactEmail,
      IsPrimary: true
    }));

    this.AddingContact = false;

    this.AvailableContacts = await lastValueFrom(
      this.outboundData.getWarehouseContacts(this.OrderDetails.WarehouseNumber)
    );
    this.SelectedContactIds = new Set(
      this.AvailableContacts.filter((c: any) => c.IsPrimary).map((c: any) => c.WarehouseContactID)
    );
  }

  // ---- Zero-price override (5.2.9), only visible/usable to Executive /
  // System Admin AD group members — see canOverrideZeroPrice above ----
  async toggleZeroPriceOverride() {
    if (!this.canOverrideZeroPrice || this.busy) return;
    const newValue = !this.OrderDetails?.ZeroPriceOverride;
    await this.runAction(async () => {
      await lastValueFrom(this.outboundData.setZeroPriceOverride(this.OrderID, newValue));
      await this.loadAll();
    });
  }

  async placeOnHold() {
    if (this.busy || this.freightChanged) { this.ActionError = 'Save freight details before placing the order on hold.'; return; }
    const reason = window.prompt('Reason for placing this order on hold:');
    if (!reason?.trim()) return;
    await this.runAction(async () => {
      await lastValueFrom(this.outboundData.placeOnHold(this.OrderID, reason.trim()));
      await this.loadAll();
    });
  }

  async releaseHold() {
    if (this.freightChanged) { this.ActionError = 'Save freight details before releasing the hold.'; return; }
    await this.runAction(async () => {
      await lastValueFrom(this.outboundData.releaseHold(this.OrderID));
      await this.loadAll();
    });
  }

  returnToList() {
    this.pulldownRef.close(this.OrderID);
  }
}
